import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import liff from '@line/liff'

import BookingPage, { type LiffContext } from './BookingPage'
import { Banner, Spinner } from './ui'

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true'
const LIFF_ID = import.meta.env.VITE_LIFF_ID || ''

/**
 * The `line` front door. LIFF endpoint URL is /liff?slug={slug}; the rich-menu /
 * OA link is https://liff.line.me/{liffId}?slug={slug}. We init the LIFF SDK,
 * ensure the user is logged in, pull the id_token (+ display name), then run the
 * normal booking flow with OTP skipped — the id_token mints the booking token.
 */
export default function LiffEntry() {
  const [sp] = useSearchParams()
  const slug = sp.get('slug') || ''
  const { t } = useTranslation()
  const [ctx, setCtx] = useState<LiffContext | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    async function run() {
      if (USE_MOCK) {
        setCtx({ idToken: 'mock-liff-id-token', name: 'LINE 用戶' })
        return
      }
      try {
        await liff.init({ liffId: LIFF_ID })
        if (!liff.isLoggedIn()) {
          liff.login({ redirectUri: window.location.href })
          return // redirecting to LINE login
        }
        const idToken = liff.getIDToken()
        if (!idToken) throw new Error('no id token')
        let name: string | undefined
        try {
          name = (await liff.getProfile()).displayName
        } catch {
          /* profile is optional; name can be entered manually */
        }
        if (alive) setCtx({ idToken, name })
      } catch {
        if (alive) setError(t('liff.error'))
      }
    }
    void run()
    return () => {
      alive = false
    }
  }, [t])

  if (!slug) {
    return (
      <div className="page">
        <Banner kind="error">{t('notFound.title')}</Banner>
      </div>
    )
  }
  if (error) {
    return (
      <div className="page">
        <Banner kind="error">{error}</Banner>
      </div>
    )
  }
  if (!ctx) {
    return (
      <div className="page">
        <Spinner />
        <p className="center muted">{t('liff.connecting')}</p>
      </div>
    )
  }
  return <BookingPage slugOverride={slug} liff={ctx} />
}
